import { AssetType, type Account } from '../../../../../../types/api';
import { INSTALLER_DOCUMENT_TYPE } from '../../../constants';
import { Asset } from '../../loan/asset';
import { LOAN } from '../../loan/constants';
import { ProjectTypes } from '../../loan/projectType';
import type { PROJECT_TYPE } from '../../loan/types';
import { getAssetTypesByContractorType, hasFlex } from '../account';

// Show/hide and required/optional rules for the Add New Applicant v2 form.
// Pure functions of (account, selectedAssetTypes), consumed by both
// the UI and the Zod schema so visibility and validation can't drift apart.

/** Classify the project; undefined = Flex installer with nothing selected yet. */
const projectType = (
  account: Account | null | undefined,
  selectedAssetTypes: ReadonlyArray<AssetType>
): PROJECT_TYPE | undefined => {
  if (!hasFlex(account)) return LOAN.PROJECT_TYPE.SOLAR;
  return ProjectTypes.forAssetTypes(selectedAssetTypes);
};

const hasOnlyStorageAssets = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean =>
  selectedAssetTypes.length === 1 &&
  selectedAssetTypes.includes(AssetType.ENERGY_STORAGE_BESS);

const hasOnlyEvChargerAssets = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean =>
  selectedAssetTypes.length === 1 &&
  selectedAssetTypes.includes(AssetType.EV_CHARGERS);

const hasSolarAsset = (selectedAssetTypes: ReadonlyArray<AssetType>): boolean =>
  selectedAssetTypes.includes(AssetType.SOLAR);

// Empty, Solar (alone or mixed), storage-only, and EV-chargers-only ask who
// pays construction interest. Other non-empty selections are Flex and skip it.
const showsConstructionInterestPayer = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean => {
  if (selectedAssetTypes.length === 0) return true;
  return (
    hasSolarAsset(selectedAssetTypes) ||
    hasOnlyStorageAssets(selectedAssetTypes) ||
    hasOnlyEvChargerAssets(selectedAssetTypes)
  );
};

const hasOnlyFlexAssets = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean => {
  const isOnlySOLAsset = hasOnlyStorageAssets(selectedAssetTypes);
  const isSolar = hasSolarAsset(selectedAssetTypes);
  const hasEvChargers = selectedAssetTypes.includes(AssetType.EV_CHARGERS);
  return (
    selectedAssetTypes.length >= 1 &&
    !isOnlySOLAsset &&
    !isSolar &&
    !hasEvChargers
  );
};

const isFlexOnlyProject = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean => hasOnlyFlexAssets(selectedAssetTypes);

const emptyProjectAssets = {
  solar: false,
  energyStorage: false,
  evChargers: false,
  roofing: false,
  hvac: false,
  ledLighting: false,
  other: false,
  otherValue: '',
  otherPrice: undefined,
};

/** A single allowed asset is pre-selected. More than one starts with nothing selected. */
const getDefaultProjectAssets = (
  account: Account | null | undefined
): typeof emptyProjectAssets => {
  const assetTypes = getAssetTypesByContractorType(account);
  if (assetTypes.length !== 1) return emptyProjectAssets;
  const option = Asset.OPTIONS.find((opt) => opt.assetType === assetTypes[0]);
  if (!option) return emptyProjectAssets;
  return { ...emptyProjectAssets, [option.key]: true };
};

/**
 * True when the project includes Solar — either explicitly selected, or
 * implicitly (Solar-only installer, or Flex installer who hasn't picked
 * yet and so defaults to Solar-flavored UI). Inverse of isFlexOnlyProject.
 *
 * Drives every "show this Solar-related section / require this
 * Solar-related field" rule below, so they all stay in lockstep.
 */
const projectHasSolar = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): boolean =>
  !isFlexOnlyProject(selectedAssetTypes) &&
  !hasOnlyStorageAssets(selectedAssetTypes) &&
  hasSolarAsset(selectedAssetTypes);

// Section visibility ---------------------------------------------------

/** Asset cards show when the account can choose among more than one asset. */
const isProjectAssetInformationSectionVisible = (
  account: Account | null | undefined
): boolean => getAssetTypesByContractorType(account).length > 1;

/** Solar Project Information section (system size, utility, mounting, etc.). */
const isSolarProjectInformationVisible = projectHasSolar;

/** Loan Type dropdown (Standard / Special Payment / Tax Credit / Flex). */
const isLoanTypeDropdownVisible = projectHasSolar;

/** Utility Bills upload section in Documents Upload. */
const isUtilityBillsSectionVisible = projectHasSolar;

// Field requirements ---------------------------------------------------

/** Utility Bills required at submit time. */
const isUtilityBillsRequired = projectHasSolar;

// Document type selection ---------------------------------------------

// BE Oracle pipeline extracts solar-specific fields from SOLAR_LOAN_PROPOSAL
// and project-general fields from PROJECT_PROPOSAL — types must stay distinct.
const proposalDocumentType = (
  selectedAssetTypes: ReadonlyArray<AssetType>
): string =>
  !projectHasSolar(selectedAssetTypes)
    ? INSTALLER_DOCUMENT_TYPE.PROJECT_PROPOSAL
    : INSTALLER_DOCUMENT_TYPE.SOLAR_LOAN_PROPOSAL;

export const BorrowerForm = {
  projectType,
  isFlexOnlyProject,
  projectHasSolar,
  hasOnlyStorageAssets,
  hasSolarAsset,
  showsConstructionInterestPayer,
  isProjectAssetInformationSectionVisible,
  isSolarProjectInformationVisible,
  isLoanTypeDropdownVisible,
  isUtilityBillsSectionVisible,
  isUtilityBillsRequired,
  proposalDocumentType,
  hasOnlyFlexAssets,
  getDefaultProjectAssets,
};
